// Undo/redo history for FlowchartBuilder, extracted out of the component body.
// Owns history/historyIndex itself (unlike useFlowchartSync's refs) because
// nothing outside this hook needs to read historyIndex directly — every call
// site only ever calls undo/redo/resetHistory/pushHistoryEntry. saveToHistory
// is internal-only (see its own comment) — every discrete, one-shot mutation
// across the app calls pushHistoryEntry directly instead, so each becomes
// exactly one undo step regardless of timing between unrelated actions.
import { useCallback, useEffect, useState } from 'react';
import type { Box, Arrow, HistoryState } from './types/index';

interface UseUndoRedoHistoryParams {
  boxes: Box[];
  arrows: Arrow[];
  setBoxes: (b: Box[]) => void;
  setArrows: (a: Arrow[]) => void;
}

export function useUndoRedoHistory(params: UseUndoRedoHistoryParams) {
  const { boxes, arrows, setBoxes, setArrows } = params;

  const [history, setHistory] = useState<HistoryState[]>([]);
  const [historyIndex, setHistoryIndex] = useState<number>(-1);

  // Internal only — the debounce effect below is the SOLE caller. Every discrete,
  // one-shot mutation goes through pushHistoryEntry instead (immediate, exact final
  // state); this stays purely as the fallback for genuinely continuous edits (a
  // pixel of a drag, a keystroke), which never call pushHistoryEntry themselves.
  const saveToHistory = useCallback((): void => {
    const newState: HistoryState = {
      boxes: JSON.parse(JSON.stringify(boxes)),
      arrows: JSON.parse(JSON.stringify(arrows))
    };

    // Remove any future history if we're not at the end
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newState);

    // Keep only last 20 states to prevent memory issues
    if (newHistory.length > 20) {
      newHistory.shift();
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    } else {
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    }
  }, [boxes, arrows, history, historyIndex]);

  const undo = useCallback((): void => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const state = history[newIndex];
      setBoxes(JSON.parse(JSON.stringify(state.boxes)));
      setArrows(JSON.parse(JSON.stringify(state.arrows)));
      setHistoryIndex(newIndex);
    }
  }, [history, historyIndex, setBoxes, setArrows]);

  const redo = useCallback((): void => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const state = history[newIndex];
      setBoxes(JSON.parse(JSON.stringify(state.boxes)));
      setArrows(JSON.parse(JSON.stringify(state.arrows)));
      setHistoryIndex(newIndex);
    }
  }, [history, historyIndex, setBoxes, setArrows]);

  // Resets the history stack to a single entry — used whenever boxes/arrows are
  // replaced wholesale from an external source (load a flowchart, restore a
  // version) rather than edited locally. Without this, Undo could jump back into
  // a PREVIOUS flowchart's content that happened to still be sitting in the stack.
  const resetHistory = useCallback((resetBoxes: Box[], resetArrows: Arrow[]): void => {
    setHistory([{ boxes: resetBoxes, arrows: resetArrows }]);
    setHistoryIndex(0);
  }, []);

  // Pushes a single history entry immediately with the FINAL (post-mutation) boxes/
  // arrows — the one mechanism every discrete, one-shot action should use (status
  // change, delete, add box/comment, cut/paste, etc.), so each becomes exactly one
  // undo step regardless of how quickly the user performs the next action. Only
  // continuous edits (a drag pixel, a keystroke) skip this and rely on the debounced
  // autosave effect below instead, which coalesces them into one entry once they
  // settle. Mixing the two for different actions is what let a quick status change
  // get silently absorbed into whichever action's immediate push landed next.
  const pushHistoryEntry = useCallback((newBoxes: Box[], newArrows: Arrow[]): void => {
    const newEntry = { boxes: JSON.parse(JSON.stringify(newBoxes)), arrows: JSON.parse(JSON.stringify(newArrows)) };
    const newHist = [...history.slice(0, historyIndex + 1), newEntry];
    const trimmedHist = newHist.length > 20 ? newHist.slice(-20) : newHist;
    setHistory(trimmedHist);
    setHistoryIndex(trimmedHist.length - 1);
  }, [history, historyIndex]);

  // Auto-save to history when boxes or arrows change (debounced), so history
  // captures the settled result of an edit rather than every intermediate value
  // (e.g. every pixel of a drag). Deliberately the ONLY path left relying on this
  // debounce — every discrete action calls pushHistoryEntry immediately instead
  // (see its own comment above); this effect exists purely for continuous edits
  // (box drag, title/duration/assignee typing) that never call it.
  useEffect(() => {
    // Skip initial render and when loading
    if (boxes.length === 0 && arrows.length === 0 && history.length === 0) return;

    const timeoutId = setTimeout(() => {
      // Only save if something actually changed
      const lastState = history[historyIndex];
      if (!lastState ||
          JSON.stringify(lastState.boxes) !== JSON.stringify(boxes) ||
          JSON.stringify(lastState.arrows) !== JSON.stringify(arrows)) {
        saveToHistory();
      }
    }, 800); // Debounce for 800ms

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boxes, arrows]); // Only trigger on boxes/arrows changes

  return { undo, redo, resetHistory, pushHistoryEntry };
}
